import { Select, Group, Text, Anchor, Loader } from '@mantine/core';
import { ExternalLink } from 'react-feather';
import type { PaperformForm } from '../../../../../services/api/feedbackSurveys';

interface PaperformSelectProps {
  forms: PaperformForm[];
  loading?: boolean;
  value: string | null;
  onChange: (value: string | null) => void;
  error?: string;
}

/**
 * Paperform selection dropdown with form details
 */
export function PaperformSelect({
  forms,
  loading,
  value,
  onChange,
  error,
}: PaperformSelectProps) {
  // Build select options from forms
  const options = forms.map((form) => ({
    value: form.id,
    label: form.title,
  }));

  // Get selected form details
  const selectedForm = forms.find((f) => f.id === value);

  const paperformLink = selectedForm
    ? { href: selectedForm.url, label: 'View Selected Form in Paperform' }
    : { href: 'https://paperform.co/dashboard', label: 'Manage Forms in Paperform' };

  return (
    <div>
      <Group justify="space-between" mb="xs">
        <Text size="xs" fw={500} c="dimmed" tt="uppercase">
          Select Form
        </Text>
        <Anchor
          href={paperformLink.href}
          target="_blank"
          size="xs"
          c="dimmed"
        >
          <Group gap={4}>
            {paperformLink.label}
            <ExternalLink size={12} />
          </Group>
        </Anchor>
      </Group>

      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading forms...</Text>
        </Group>
      ) : (
        <>
          <Select
            placeholder="Select a form"
            data={options}
            value={value}
            onChange={onChange}
            searchable
            nothingFoundMessage="No forms found"
            error={error}
          />

          {selectedForm && (
            <Text size="xs" c="dimmed" mt="xs">
              {selectedForm.status === 'active' ? '✓ Active' : '⚠ Draft'} · Last updated{' '}
              {new Date(selectedForm.updated_at).toLocaleDateString()}
            </Text>
          )}
        </>
      )}
    </div>
  );
}

export default PaperformSelect;
